import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StatBar, StorageModeFilter, useStorageModeFilter } from '~/components/common';
import { ISafetyAlertsResponse } from '~/utils/interface/analytics';

const SafetyAlerts = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ISafetyAlertsResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const storageMode = useStorageModeFilter();

  useEffect(() => {
    analyticsApi
      .getSafetyAlerts(storageMode)
      .then((res) => setData(res.data.data))
      .catch(() => setError('pages.safetyAlerts.loadError'))
      .finally(() => setIsLoading(false));
  }, [storageMode]);

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.safetyAlerts.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.safetyAlerts.description')}</p>
      <StorageModeFilter />

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{t(error)}</p>}

      {data && (
        <div className='mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3'>
          <div className='flex flex-col gap-2 rounded-lg border border-border bg-card p-4 sm:p-6'>
            <h2 className='text-base font-semibold text-foreground'>{t('pages.safetyAlerts.sos')}</h2>
            <p className='text-3xl font-semibold text-foreground'>{data.sosActivated}</p>
            <p className='text-sm text-muted-foreground'>{t('pages.safetyAlerts.timesActivated')}</p>
          </div>

          <div className='flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
            <h2 className='text-base font-semibold text-foreground'>{t('pages.safetyAlerts.fallDetection')}</h2>
            <p className='text-sm text-muted-foreground'>
              {t('pages.safetyAlerts.detected', { count: data.fall.detected })}
            </p>
            <StatBar
              label={t('pages.safetyAlerts.alertSent')}
              value={data.fall.alertSucceeded}
              total={data.fall.alertSucceeded + data.fall.alertFailed || 1}
            />
            <StatBar
              label={t('pages.safetyAlerts.alertFailed')}
              value={data.fall.alertFailed}
              total={data.fall.alertSucceeded + data.fall.alertFailed || 1}
              tone='destructive'
            />
          </div>

          <div className='flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
            <h2 className='text-base font-semibold text-foreground'>
              {t('pages.safetyAlerts.inactivityDetection')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('pages.safetyAlerts.detected', { count: data.inactivity.detected })}
            </p>
            <StatBar
              label={t('pages.safetyAlerts.alertSent')}
              value={data.inactivity.alertSucceeded}
              total={data.inactivity.alertSucceeded + data.inactivity.alertFailed || 1}
            />
            <StatBar
              label={t('pages.safetyAlerts.alertFailed')}
              value={data.inactivity.alertFailed}
              total={data.inactivity.alertSucceeded + data.inactivity.alertFailed || 1}
              tone='destructive'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyAlerts;
